// Animation route — MP4 encoder (WebCodecs + mp4-muxer).
//
// Steps the timeline deterministically (frame i → t = i/fps), draws each
// frame into an offscreen canvas via drawAnimationFrame, encodes H.264
// via the browser's VideoEncoder, and muxes into an in-memory MP4 with
// `fastStart` so the moov atom sits up front (required for instant
// playback on social platforms + the gallery's <video> tiles).
//
// Runs entirely client-side — no server, no upload during encode. A
// 15s 9:16 @30fps export encodes in a few seconds on a modern laptop.
//
// Browser support: VideoEncoder (WebCodecs) is in Chrome/Edge 94+,
// Safari 16.4+, Firefox 130+. isAnimationExportSupported() gates the
// Export button; unsupported browsers get a "use Chrome/Safari" notice
// instead of a cryptic failure.

import { ArrayBufferTarget, Muxer } from 'mp4-muxer';
import { drawAnimationFrame } from './render';
import { ANIM_FPS, specDurationMs, type AnimationSpec } from './types';

export function isAnimationExportSupported(): boolean {
  return typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined';
}

// H.264 High profile, level 5.0 — comfortably covers 1080×1920 @ 30fps
// (level 4.0 is borderline at that frame size; 5.0 leaves headroom and
// every hardware encoder that supports High@4.x supports 5.0 too).
const AVC_CODEC = 'avc1.640032';
// Peak bitrate for SHORT clips — 8 Mbps is crisp for 1080×1920 social.
const BITRATE_MAX = 8_000_000;
// Absolute floor for encoder sanity + a quality baseline. Low enough that the
// size budget below always wins within any realistic clip length (at 2.5 Mbps
// the 27MB budget still holds up to ~86s — well past this tool's ~30s max).
const BITRATE_MIN = 2_500_000;
// File-size budget (bytes). Video posts upload through /api/gallery/posts, which
// the backend caps at 31MB (just under Cloud Run's 32MB request ceiling). We aim
// under that with headroom so the browser → backend hop always fits. At a target
// budget T over duration D seconds, bitrate ≈ T·8/D; a short clip keeps the full
// 8 Mbps, a long clip scales down so the file still lands under the cap. (The
// durable fix for truly unbounded length is a signed-URL direct-to-GCS upload
// that bypasses the request body entirely.)
const TARGET_MAX_BYTES = 27 * 1024 * 1024; // 27 MB — leaves ~4MB under the 31MB cap

/** Pick an H.264 bitrate that keeps the encoded file under TARGET_MAX_BYTES for
 *  the given duration, clamped to [BITRATE_MIN, BITRATE_MAX]. */
function bitrateForDuration(durationMs: number): number {
  const seconds = Math.max(0.5, durationMs / 1000);
  const budgetBps = Math.floor((TARGET_MAX_BYTES * 8) / seconds);
  return Math.max(BITRATE_MIN, Math.min(BITRATE_MAX, budgetBps));
}

export interface EncodeOptions {
  spec: AnimationSpec;
  width: number;
  height: number;
  /** 0..1 progress callback for the export UI. */
  onProgress?: (frac: number) => void;
}

/** Render + encode the animation to an MP4 Blob. Throws with a
 *  user-readable message on unsupported browsers / encoder failure. */
export async function encodeAnimationToMp4(opts: EncodeOptions): Promise<Blob> {
  const { spec, width, height, onProgress } = opts;
  if (!isAnimationExportSupported()) {
    throw new Error(
      'Video export needs a recent Chrome, Edge, Safari, or Firefox.',
    );
  }

  // Fonts must be live before any measureText/fillText — fallback-font
  // frames would differ from the preview (same rule as composeRender).
  await document.fonts.ready;

  // Duration-aware bitrate: keeps the output under the backend upload cap
  // regardless of clip length (full quality for short clips, gracefully
  // lower for long ones).
  const durationMs = specDurationMs(spec);
  const bitrate = bitrateForDuration(durationMs);

  const support = await VideoEncoder.isConfigSupported({
    codec: AVC_CODEC,
    width,
    height,
    bitrate,
    framerate: ANIM_FPS,
  });
  if (!support.supported) {
    throw new Error('This browser cannot encode H.264 video at this size.');
  }

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: { codec: 'avc', width, height },
    fastStart: 'in-memory',
  });

  let encodeError: Error | null = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => {
      encodeError = e instanceof Error ? e : new Error(String(e));
    },
  });
  encoder.configure({
    codec: AVC_CODEC,
    width,
    height,
    bitrate,
    framerate: ANIM_FPS,
  });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  const totalFrames = Math.max(1, Math.round((durationMs / 1000) * ANIM_FPS));
  const frameUs = 1_000_000 / ANIM_FPS;

  for (let i = 0; i < totalFrames; i++) {
    if (encodeError) break;
    const tMs = (i * 1000) / ANIM_FPS;
    drawAnimationFrame(ctx, spec, tMs, width, height);
    const frame = new VideoFrame(canvas, {
      timestamp: Math.round(i * frameUs),
      duration: Math.round(frameUs),
    });
    // Keyframe every 2s — players seek cleanly, social transcoders happy.
    encoder.encode(frame, { keyFrame: i % (ANIM_FPS * 2) === 0 });
    frame.close();

    // Backpressure: don't let the encode queue run away on slow
    // hardware. Also yields to the event loop so the progress UI paints.
    while (encoder.encodeQueueSize > 4) {
      await new Promise((r) => setTimeout(r, 0));
      if (encodeError) break;
    }
    if (i % 5 === 0) {
      onProgress?.(i / totalFrames);
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  if (encodeError) {
    encoder.close();
    throw new Error(`Video encoding failed: ${(encodeError as Error).message}`);
  }

  await encoder.flush();
  encoder.close();
  muxer.finalize();
  onProgress?.(1);

  return new Blob([muxer.target.buffer], { type: 'video/mp4' });
}
